
import React, { useState } from 'react';
import { IconBuilding, IconBriefcase, IconRisk, IconSparkles } from '../components/Icons';
import { generateCreditMemo } from '../services/geminiService';
import toast from 'react-hot-toast';

interface Counterparty {
    id: string;
    name: string;
    rating: string;
    outlook: 'Stable' | 'Negative' | 'Positive';
    exposure: number;
    pfe: number; // Potential Future Exposure
    collateral: number;
    limit: number;
    sentiment: string; // from News Scanner
}

const MOCK_CPTYS: Counterparty[] = [
    { id: 'CP-001', name: 'J.P. Morgan', rating: 'AA-', outlook: 'Stable', exposure: 12500000, pfe: 18000000, collateral: 13000000, limit: 25000000, sentiment: 'Neutral' },
    { id: 'CP-002', name: 'Goldman Sachs', rating: 'A+', outlook: 'Stable', exposure: 8500000, pfe: 14000000, collateral: 9000000, limit: 20000000, sentiment: 'Positive' },
    { id: 'CP-003', name: 'Deutsche Bank', rating: 'BBB+', outlook: 'Negative', exposure: 4200000, pfe: 9000000, collateral: 4500000, limit: 10000000, sentiment: 'Bearish' },
    { id: 'CP-004', name: 'Citibank', rating: 'A', outlook: 'Stable', exposure: 15000000, pfe: 22000000, collateral: 14500000, limit: 25000000, sentiment: 'Neutral' },
];

const CounterpartyView: React.FC = () => {
    const [selectedCpty, setSelectedCpty] = useState<Counterparty | null>(null);
    const [memo, setMemo] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);

    const handleSelect = (cpty: Counterparty) => {
        setSelectedCpty(cpty);
        setMemo('');
    };

    const handleGenerateMemo = async () => {
        if (!selectedCpty) return;
        setIsGenerating(true);
        
        const data = {
            currentExposure: `$${(selectedCpty.exposure/1000000).toFixed(1)}M`,
            pfe: `$${(selectedCpty.pfe/1000000).toFixed(1)}M`,
            collateral: `$${(selectedCpty.collateral/1000000).toFixed(1)}M`
        };
        
        const result = await generateCreditMemo(selectedCpty.name, data, selectedCpty.sentiment);
        setMemo(result);
        setIsGenerating(false);
    };

    const getUtilizationColor = (val: number, max: number) => {
        const pct = (val / max) * 100;
        if (pct > 90) return 'bg-red-500';
        if (pct > 75) return 'bg-amber-500';
        return 'bg-blue-500';
    };

    return (
        <div className="flex h-full bg-slate-100">
            {/* Left: Directory */}
            <div className="w-80 bg-white border-r border-slate-200 flex flex-col">
                <div className="p-6 border-b border-slate-100">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
                        <IconBuilding /> Counterparty 360
                    </h2>
                    <p className="text-xs text-slate-500">Credit Master & Exposure Management</p>
                </div>
                
                <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1">
                    {MOCK_CPTYS.map(cpty => (
                        <div 
                            key={cpty.id}
                            onClick={() => handleSelect(cpty)}
                            className={`p-3 rounded border cursor-pointer transition-all ${
                                selectedCpty?.id === cpty.id 
                                ? 'bg-blue-50 border-blue-500 shadow-sm' 
                                : 'bg-white border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            <div className="flex justify-between items-center mb-1">
                                <span className="font-bold text-sm text-slate-800">{cpty.name}</span>
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                    cpty.rating.startsWith('A') ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                                }`}>{cpty.rating}</span>
                            </div>
                            <div className="flex justify-between text-xs text-slate-500">
                                <span>Exp: ${(cpty.exposure/1000000).toFixed(1)}M</span>
                                <span className={cpty.outlook === 'Negative' ? 'text-red-500' : 'text-slate-400'}>{cpty.outlook}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right: Profile */}
            <div className="flex-1 p-8 overflow-y-auto custom-scrollbar">
                {!selectedCpty ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400 opacity-60">
                        <div className="text-6xl mb-4"><IconBuilding /></div>
                        <p className="text-lg">Select a counterparty to view credit profile.</p>
                    </div>
                ) : (
                    <div className="space-y-8 max-w-4xl mx-auto">
                        {/* Header Card */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex justify-between items-start">
                            <div>
                                <h1 className="text-3xl font-bold text-slate-900 mb-1">{selectedCpty.name}</h1>
                                <div className="flex items-center gap-3 text-sm text-slate-500">
                                    <span className="flex items-center gap-1"><IconBriefcase /> Master Agreement: ISDA 2002</span>
                                    <span>•</span>
                                    <span>LEI: 549300...</span>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-xs text-slate-400 uppercase font-bold mb-1">Internal Rating</div>
                                <div className="text-4xl font-bold text-slate-800">{selectedCpty.rating}</div>
                                <div className={`text-xs font-bold mt-1 ${selectedCpty.outlook === 'Stable' ? 'text-green-600' : 'text-red-600'}`}>
                                    {selectedCpty.outlook.toUpperCase()} OUTLOOK
                                </div>
                            </div>
                        </div>

                        {/* Exposure Metrics */}
                        <div className="grid grid-cols-3 gap-6">
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                <div className="text-xs text-slate-500 uppercase font-bold mb-2">Current Exposure (MtM)</div>
                                <div className="text-2xl font-bold text-slate-800">${(selectedCpty.exposure/1000000).toFixed(2)}M</div>
                                <div className="w-full bg-slate-100 h-1.5 mt-3 rounded-full overflow-hidden">
                                    <div className="h-full bg-blue-500" style={{ width: '65%' }}></div>
                                </div>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                <div className="text-xs text-slate-500 uppercase font-bold mb-2">Potential Future Exposure (PFE)</div>
                                <div className="text-2xl font-bold text-slate-800">${(selectedCpty.pfe/1000000).toFixed(2)}M</div>
                                <div className="text-xs text-slate-400 mt-1">95% Confidence Interval</div>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                <div className="text-xs text-slate-500 uppercase font-bold mb-2">Credit Limit Utilization</div>
                                <div className="text-2xl font-bold text-slate-800">
                                    {((selectedCpty.exposure / selectedCpty.limit) * 100).toFixed(1)}%
                                </div>
                                <div className="w-full bg-slate-100 h-1.5 mt-3 rounded-full overflow-hidden">
                                    <div 
                                        className={`h-full ${getUtilizationColor(selectedCpty.exposure, selectedCpty.limit)}`} 
                                        style={{ width: `${(selectedCpty.exposure / selectedCpty.limit) * 100}%` }}
                                    ></div>
                                </div>
                                <div className="text-xs text-slate-400 mt-1 text-right">
                                    Limit: ${(selectedCpty.limit/1000000).toFixed(0)}M
                                </div>
                            </div>
                        </div>

                        {/* AI Credit Memo */}
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                            <div className="bg-slate-900 p-4 flex justify-between items-center">
                                <h3 className="text-white font-bold flex items-center gap-2">
                                    <IconSparkles /> AiQ Credit Review
                                </h3>
                                <button 
                                    onClick={handleGenerateMemo}
                                    disabled={isGenerating}
                                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-3 py-1.5 rounded transition-colors"
                                >
                                    {isGenerating ? 'Drafting...' : 'Generate Memo'}
                                </button>
                            </div>
                            <div className="p-8 min-h-[200px]">
                                {memo ? (
                                    <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed whitespace-pre-line font-serif">
                                        {memo}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-full text-slate-400 py-8">
                                        <div className="text-4xl mb-2 opacity-20"><IconBriefcase /></div>
                                        <p className="text-sm italic">Click generate to draft a formal credit review for the Risk Committee.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CounterpartyView;
